import { useState, useEffect } from 'react';
import { FaTimes, FaUser, FaGraduationCap, FaSchool, FaEdit, FaCheck, FaSearch } from 'react-icons/fa';
import { updateStudent, listSchools } from '@dataconnect/generated';

const ProfileModal = ({ isOpen, onClose, student, onProfileUpdate }) => {
  const [editingField, setEditingField] = useState(null); // 'name', 'grade', 'school'
  const [tempValue, setTempValue] = useState('');
  const [tempExtraData, setTempExtraData] = useState(null);
  const [schools, setSchools] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && student) {
      setEditingField(null);
      setError('');
    }
  }, [isOpen, student]);

  useEffect(() => {
    if (isOpen && editingField === 'school') {
      const fetchSchools = async () => {
        try {
          const result = await listSchools({ limit: 5000 });
          if (result.data?.schools) {
            setSchools(result.data.schools.filter(school => school.isActive));
          }
        } catch (err) {
          console.error('Failed to load schools:', err);
        }
      };
      fetchSchools();
    }
  }, [isOpen, editingField]);

  const filteredSchools = schools.filter(school => {
    const searchLower = schoolSearch.toLowerCase().trim();
    if (!searchLower) return true;

    // Split search query into individual words, filtering out empty strings
    const searchWords = searchLower.split(/[^a-z0-9]/i).filter(Boolean);
    
    // Combine all searchable content for this school
    const searchableText = (
      school.searchField || 
      `${school.name} ${school.address || ''} ${school.district || ''} ${school.state || ''}`
    ).toLowerCase();

    // Every search word must be found somewhere in the school's combined content
    return searchWords.every(word => searchableText.includes(word));
  });

  const startEditing = (field) => {
    setEditingField(field);
    setError('');
    if (field === 'name') setTempValue(student.name);
    if (field === 'grade') setTempValue(student.gradeLevel);
    if (field === 'school') {
      setTempValue(student.school?.id || '');
      setSchoolSearch(''); // Clear search to show all schools initially
      setShowSchoolDropdown(true);
    }
  };

  const cancelEditing = () => {
    setEditingField(null);
    setTempValue('');
    setTempExtraData(null);
    setSchoolSearch('');
  };

  const handleSaveField = async (field, value, extraData = null) => {
    if (!student?.id) return;
    
    setSaving(true);
    setError('');

    try {
      const updateParams = { id: student.id };
      if (field === 'name') updateParams.name = value;
      if (field === 'grade') updateParams.gradeLevel = parseInt(value);
      if (field === 'school') updateParams.schoolId = value;

      const result = await updateStudent(updateParams);

      if (result.data?.student_update) {
        if (onProfileUpdate) {
          const updates = {};
          if (field === 'name') updates.name = value;
          if (field === 'grade') updates.gradeLevel = parseInt(value);
          if (field === 'school' && extraData) updates.school = extraData;
          
          // Call parent update and wait for it to complete if it's a promise
          await onProfileUpdate(updates); 
        }
        setEditingField(null);
        setTempValue('');
      }
    } catch (err) {
      console.error('Failed to update student field:', err);
      setError(`Failed to save ${field}. Please try again.`);
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => {
          e.stopPropagation();
          setShowSchoolDropdown(false);
        }}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <h2 className="text-2xl font-bold">Student Profile</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close profile"
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm">
              {error}
            </div>
          )}

          {/* Student Info Section */}
          <div className="bg-indigo-50 rounded-xl p-6 border border-indigo-100 space-y-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-indigo-600 rounded-full flex items-center justify-center text-white text-2xl shadow-lg">
                <FaUser />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between group">
                  {editingField === 'name' ? (
                    <div className="flex items-center gap-2 w-full">
                      <input
                        type="text"
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        className="flex-1 px-3 py-1 bg-white border border-indigo-300 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-900"
                        autoFocus
                      />
                      <button 
                        onClick={() => handleSaveField('name', tempValue)}
                        disabled={saving}
                        className="p-2 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                      >
                        <FaCheck />
                      </button>
                      <button 
                        onClick={cancelEditing}
                        className="p-2 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <>
                      <div>
                        <h3 className="text-2xl font-bold text-gray-900">{student?.name || 'Student Name'}</h3>
                        <p className="text-indigo-600 font-medium text-sm">Student Profile</p>
                      </div>
                      <button 
                        onClick={() => startEditing('name')}
                        className="p-2 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-full transition-all opacity-0 group-hover:opacity-100"
                        aria-label="Edit name"
                      >
                        <FaEdit />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex flex-col gap-4 pt-2">
              {/* Grade Level Field */}
              <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-indigo-100 shadow-sm w-full group">
                <div className="p-2 bg-indigo-100 rounded-md text-indigo-600">
                  <FaGraduationCap />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Grade Level</p>
                  {editingField === 'grade' ? (
                    <div className="flex items-center gap-2 mt-1">
                      <select
                        value={tempValue}
                        onChange={(e) => setTempValue(e.target.value)}
                        className="flex-1 px-3 py-1 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-900"
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(grade => (
                          <option key={grade} value={grade}>Grade {grade}</option>
                        ))}
                      </select>
                      <button 
                        onClick={() => handleSaveField('grade', tempValue)}
                        disabled={saving}
                        className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                      >
                        <FaCheck />
                      </button>
                      <button 
                        onClick={cancelEditing}
                        className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-gray-900 font-bold">Grade {student?.gradeLevel || 'N/A'}</p>
                      <button 
                        onClick={() => startEditing('grade')}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-all opacity-0 group-hover:opacity-100"
                        aria-label="Edit grade"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* School Field */}
              <div className="flex items-center gap-3 bg-white p-3 rounded-lg border border-indigo-100 shadow-sm w-full group">
                <div className="p-2 bg-purple-100 rounded-md text-purple-600">
                  <FaSchool />
                </div>
                <div className="min-w-0 flex-1 relative">
                  <p className="text-xs text-gray-500 uppercase tracking-wider font-semibold">School</p>
                  {editingField === 'school' ? (
                    <div className="relative mt-1" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-2">
                        <div className="relative flex-1">
                          <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                          <input
                            type="text"
                            value={schoolSearch}
                            onChange={(e) => {
                              setSchoolSearch(e.target.value);
                              setShowSchoolDropdown(true);
                            }}
                            onFocus={() => setShowSchoolDropdown(true)}
                            className="w-full pl-10 pr-4 py-1.5 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none font-bold text-gray-900 text-sm"
                            placeholder="Search for your school..."
                            autoFocus
                          />
                        </div>
                        {tempValue && tempValue !== student.school?.id && (
                          <button 
                            onClick={() => handleSaveField('school', tempValue, tempExtraData)}
                            disabled={saving}
                            className="p-1.5 text-green-600 hover:bg-green-50 rounded-full transition-colors"
                            title="Save school"
                          >
                            {saving ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-green-600"></div>
                            ) : (
                              <FaCheck />
                            )}
                          </button>
                        )}
                        <button 
                          onClick={cancelEditing}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-full transition-colors"
                          title="Cancel"
                        >
                          <FaTimes />
                        </button>
                      </div>
                      
                      {showSchoolDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-xl max-h-60 overflow-y-auto">
                          {filteredSchools.length > 0 ? (
                            filteredSchools.map(school => (
                              <button
                                key={school.id}
                                onClick={() => {
                                  setTempValue(school.id);
                                  setTempExtraData(school);
                                  setSchoolSearch(school.name);
                                  setShowSchoolDropdown(false);
                                }}
                                className="w-full text-left px-4 py-3 hover:bg-indigo-50 transition-colors border-b border-gray-100 last:border-0 flex flex-col gap-1"
                              >
                                <div className="flex items-center gap-2">
                                  <FaSchool className="text-indigo-400 text-xs" />
                                  <p className="font-bold text-gray-900">{school.name}</p>
                                </div>
                                {school.address && (
                                  <p className="text-xs text-gray-500 truncate pl-5">{school.address}</p>
                                )}
                                <p className="text-[10px] text-indigo-600 font-bold uppercase tracking-wider pl-5">
                                  {school.district}{school.district && school.state ? ' • ' : ''}{school.state}
                                </p>
                              </button>
                            ))
                          ) : (
                            <div className="p-4 text-center text-gray-500 text-sm italic">No schools found</div>
                          )}
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-gray-900 font-bold break-words pr-2" title={student?.school?.name || 'No School'}>
                        {student?.school?.name || 'No School'}
                      </p>
                      <button 
                        onClick={() => startEditing('school')}
                        className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-full transition-all opacity-0 group-hover:opacity-100"
                        aria-label="Edit school"
                      >
                        <FaEdit />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-2xl border-t border-gray-200">
          <button
            onClick={onClose}
            className="w-full px-6 py-3 bg-indigo-600 text-white rounded-xl font-semibold hover:bg-indigo-700 transition-all duration-300 shadow-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileModal;
